/**
 * Per-day local weather for The Brief, "here's how the day looks where you are".
 * Open-Meteo (free, no key) for the forecast + recent days, with a coarse, prompt-free
 * IP location (ipapi.co) so we never nag for geolocation permission. Everything is
 * best-effort and cached: weather must never block or break the Brief.
 */
export interface DayWeather {
  city: string;
  tMax: number;
  tMin: number;
  label: string;
  emoji: string;
  unit: string;
}

const WMO: Record<number, { label: string; emoji: string }> = {
  0: { label: "Clear sky", emoji: "☀️" },
  1: { label: "Mostly clear", emoji: "🌤️" },
  2: { label: "Partly cloudy", emoji: "⛅" },
  3: { label: "Overcast", emoji: "☁️" },
  45: { label: "Fog", emoji: "🌫️" },
  48: { label: "Rime fog", emoji: "🌫️" },
  51: { label: "Light drizzle", emoji: "🌦️" },
  53: { label: "Drizzle", emoji: "🌦️" },
  55: { label: "Heavy drizzle", emoji: "🌧️" },
  61: { label: "Light rain", emoji: "🌦️" },
  63: { label: "Rain", emoji: "🌧️" },
  65: { label: "Heavy rain", emoji: "🌧️" },
  66: { label: "Freezing rain", emoji: "🌧️" },
  67: { label: "Freezing rain", emoji: "🌧️" },
  71: { label: "Light snow", emoji: "🌨️" },
  73: { label: "Snow", emoji: "❄️" },
  75: { label: "Heavy snow", emoji: "❄️" },
  77: { label: "Snow grains", emoji: "🌨️" },
  80: { label: "Showers", emoji: "🌦️" },
  81: { label: "Showers", emoji: "🌧️" },
  82: { label: "Violent showers", emoji: "⛈️" },
  85: { label: "Snow showers", emoji: "🌨️" },
  86: { label: "Snow showers", emoji: "❄️" },
  95: { label: "Thunderstorm", emoji: "⛈️" },
  96: { label: "Thunderstorm", emoji: "⛈️" },
  99: { label: "Hailstorm", emoji: "⛈️" },
};

interface Loc {
  lat: number;
  lon: number;
  city: string;
}

async function getLocation(): Promise<Loc | null> {
  try {
    const cached = localStorage.getItem("insnip.loc");
    if (cached) {
      const c = JSON.parse(cached) as Loc;
      if (c && typeof c.lat === "number") return c;
    }
  } catch {
    /* ignore */
  }
  try {
    const res = await fetch("https://ipapi.co/json/");
    if (!res.ok) return null;
    const j = (await res.json()) as { latitude?: number; longitude?: number; city?: string; region?: string };
    if (typeof j.latitude !== "number" || typeof j.longitude !== "number") return null;
    const loc: Loc = { lat: j.latitude, lon: j.longitude, city: j.city || j.region || "" };
    try {
      localStorage.setItem("insnip.loc", JSON.stringify(loc));
    } catch {
      /* ignore */
    }
    return loc;
  } catch {
    return null;
  }
}

// Memoised per-day so flipping briefs doesn't refetch the same day.
const cache = new Map<string, DayWeather | null>();

/** Weather for `dayISO` (YYYY-MM-DD) at the reader's location. null if unavailable. */
export async function getBriefWeather(dayISO: string): Promise<DayWeather | null> {
  const day = (dayISO || "").slice(0, 10);
  if (!day) return null;
  if (cache.has(day)) return cache.get(day) ?? null;
  const loc = await getLocation();
  if (!loc) {
    cache.set(day, null);
    return null;
  }
  try {
    const url =
      `https://api.open-meteo.com/v1/forecast?latitude=${loc.lat}&longitude=${loc.lon}` +
      `&daily=weathercode,temperature_2m_max,temperature_2m_min&timezone=auto&past_days=14&forecast_days=3`;
    const res = await fetch(url);
    if (!res.ok) {
      cache.set(day, null);
      return null;
    }
    const j = (await res.json()) as {
      daily?: { time?: string[]; weathercode?: number[]; temperature_2m_max?: number[]; temperature_2m_min?: number[] };
    };
    const times = j.daily?.time ?? [];
    let idx = times.indexOf(day);
    if (idx < 0) idx = times.length - 1; // fall back to the most recent available day
    if (idx < 0 || !j.daily) {
      cache.set(day, null);
      return null;
    }
    const code = j.daily.weathercode?.[idx] ?? 0;
    const w = WMO[code] ?? { label: "-", emoji: "🌡️" };
    const out: DayWeather = {
      city: loc.city,
      tMax: Math.round(j.daily.temperature_2m_max?.[idx] ?? 0),
      tMin: Math.round(j.daily.temperature_2m_min?.[idx] ?? 0),
      label: w.label,
      emoji: w.emoji,
      unit: "°C",
    };
    cache.set(day, out);
    return out;
  } catch {
    cache.set(day, null);
    return null;
  }
}
